import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Platform } from 'react-native';
import { authService } from '../services/authService';
import { tokenManager } from '../services/api';
import { User } from '../types';
import { getSSOLoginUrl, loadSSOConfig } from '../constants/config';
import { getAndClearIntendedUrl } from '../navigation/RootNavigator';
import {
  ACCESS_DENIED_EVENT,
  AccessDenial,
  parseAccessDenial,
} from '../constants/accessDenial';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  accessDenial: AccessDenial | null;
  clearAccessDenial: () => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

/**
 * Shallow-compare two user objects to avoid unnecessary re-renders.
 * Returns true if the user data that matters for the UI has changed.
 */
function hasUserChanged(prev: User | null, next: User | null): boolean {
  if (prev === next) return false;
  if (!prev || !next) return true;
  // Compare the fields that actually affect UI rendering
  return (
    prev.id !== next.id ||
    prev.email !== next.email ||
    prev.full_name !== next.full_name ||
    prev.profile?.user_type !== next.profile?.user_type ||
    prev.profile?.organization !== next.profile?.organization ||
    prev.data_entry_open !== next.data_entry_open
  );
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Extracts OAuth `code` param from the current URL and cleans it up.
 * Returns the code if found, null otherwise.
 */
function extractAndCleanOAuthCode(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  if (code) {
    // Remove the code from URL to prevent re-use on refresh
    params.delete('code');
    params.delete('state');
    const cleanPath = window.location.pathname;
    const remaining = params.toString();
    const cleanUrl = remaining ? `${cleanPath}?${remaining}` : cleanPath;
    window.history.replaceState(null, '', cleanUrl);
  }

  return code;
}

/**
 * Immediately redirect to SSO login page. No intermediate screen.
 *
 * Uses window.location.replace (not .href = url) so the original in-app URL
 * is REPLACED in browser history rather than stacked under the SSO URL.
 * Without this, pressing browser ← from any post-login app page (e.g.
 * /strategy) lands on the SSO URL, which then sees the user is already
 * authenticated and 302s straight back to /auth/callback → /strategy. The
 * net effect is "back is stuck on strategy".
 */
function redirectToSSOLogin(): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.replace(getSSOLoginUrl());
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenial, setAccessDenial] = useState<AccessDenial | null>(null);
  const authCheckDone = useRef(false);
  const isLoggedIn = useRef(false);

  const clearAccessDenial = () => setAccessDenial(null);
  const lastFocusRefresh = useRef(0);
  const userRef = useRef<User | null>(null);

  // Wrapper that only triggers a re-render when user data actually changed
  const setUserIfChanged = (newUser: User | null) => {
    if (hasUserChanged(userRef.current, newUser)) {
      userRef.current = newUser;
      setUser(newUser);
    }
  };

  useEffect(() => {
    if (!authCheckDone.current) {
      authCheckDone.current = true;
      initAuth();
    }
  }, []);

  // Proactive token refresh — refresh the access token before it expires
  // to prevent 401 errors during normal operation.
  useEffect(() => {
    if (!user) return;

    const checkAndRefresh = async () => {
      const accessToken = await tokenManager.getAccessToken();
      if (!accessToken) return;

      try {
        const parts = accessToken.split('.');
        if (parts.length !== 3) return;
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (!payload.exp) return;

        const expiresInMs = payload.exp * 1000 - Date.now();
        // Refresh if token expires within the next 2 minutes
        if (expiresInMs > 0 && expiresInMs < 2 * 60 * 1000) {
          await authService.refreshToken();
          // Token is stored in tokenManager — no need to trigger a re-render
        }
      } catch {
        // Failed — the 401 interceptor will handle it on the next request
      }
    };

    // Check every 30 seconds
    const intervalId = setInterval(checkAndRefresh, 30_000);
    // Also check immediately
    checkAndRefresh();

    return () => clearInterval(intervalId);
  }, [user]);

  // Listen for session expiry from the axios interceptor (refresh token also expired)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const handleSessionExpired = async () => {
      isLoggedIn.current = false;
      setUser(null);
      setToken(null);
      redirectToSSOLogin();
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  // Listen for SSO access-denial 403s (citizen_blocked, position_type_missing,
  // account_inactive, no_role, no_system_access). Tokens have already been
  // cleared by the axios interceptor — we just need to flip the UI into the
  // access-denied screen instead of bouncing back to SSO login.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const handleAccessDenied = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail) return;
      isLoggedIn.current = false;
      setUser(null);
      setToken(null);
      setAccessDenial(detail);
    };

    window.addEventListener(ACCESS_DENIED_EVENT, handleAccessDenied);
    return () => window.removeEventListener(ACCESS_DENIED_EVENT, handleAccessDenied);
  }, []);

  // Re-fetch user profile (including user_type/roles) when the tab regains focus.
  // This picks up role changes made in the SSO admin panel without requiring a
  // full re-login. Throttled to at most once every 5 minutes so rapid tab
  // switches don't spam /auth/me — roles rarely change mid-session, and any
  // staleness beyond 5 minutes is still tolerable for an admin panel change.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;

    const THROTTLE_MS = 5 * 60 * 1000; // 5 minutes

    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!isLoggedIn.current) return;
      const now = Date.now();
      if (now - lastFocusRefresh.current < THROTTLE_MS) return;
      lastFocusRefresh.current = now;
      try {
        const userData = await authService.getCurrentUser();
        setUserIfChanged(userData);
      } catch {
        // Silently ignore — stale data is preferable to crashing
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, []);

  const initAuth = async () => {
    try {
      // Load SSO config from backend (gets client_id from server env at runtime)
      await loadSSOConfig();

      // Step 0: Check for SSO-emitted access-denial error on the callback URL.
      // (Most login-time denials are handled by the SSO frontend itself, but
      // direct-link or edge-case flows can still land here with `?error=...`.)
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const errParam = urlParams.get('error');
        const denialFromUrl = errParam ? parseAccessDenial({ code: errParam }) : null;
        if (denialFromUrl) {
          urlParams.delete('error');
          urlParams.delete('error_description');
          const remaining = urlParams.toString();
          const cleanUrl = remaining
            ? `${window.location.pathname}?${remaining}`
            : window.location.pathname;
          window.history.replaceState(null, '', cleanUrl);
          await tokenManager.clearTokens();
          setAccessDenial(denialFromUrl);
          setIsLoading(false);
          return;
        }
      }

      // Step 1: Check for OAuth callback code in URL
      const code = extractAndCleanOAuthCode();

      if (code) {
        // Exchange code for tokens
        await authService.exchangeCode(code);
        const userData = await authService.getCurrentUser();
        const accessToken = await tokenManager.getAccessToken();
        setUser(userData);
        setToken(accessToken);
        isLoggedIn.current = true;

        // Restore intended URL if any
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const intendedUrl = getAndClearIntendedUrl();
          if (intendedUrl && intendedUrl !== '/' && intendedUrl !== '/auth/callback') {
            window.history.replaceState(null, '', intendedUrl);
          } else {
            window.history.replaceState(null, '', '/');
          }
        }

        setIsLoading(false);
        return;
      }

      // Step 2: Check for existing token
      const hasToken = await tokenManager.hasValidToken();

      if (!hasToken) {
        // No token, no code → redirect to SSO immediately
        setIsLoading(false);
        redirectToSSOLogin();
        return;
      }

      // Step 3: Validate existing token
      const isValid = await authService.verifyToken();

      if (isValid) {
        const userData = await authService.getCurrentUser();
        const accessToken = await tokenManager.getAccessToken();
        setUser(userData);
        setToken(accessToken);
        isLoggedIn.current = true;
      } else {
        // Try to refresh
        try {
          await authService.refreshToken();
          const userData = await authService.getCurrentUser();
          const newAccessToken = await tokenManager.getAccessToken();
          setUser(userData);
          setToken(newAccessToken);
          isLoggedIn.current = true;
        } catch {
          // Refresh failed → clear and redirect to SSO
          await tokenManager.clearTokens();
          setUser(null);
          setToken(null);
          redirectToSSOLogin();
          return;
        }
      }
    } catch (error: any) {
      console.error('Auth initialization failed:', error);
      // If the failure was an SSO access denial (citizen_blocked etc.),
      // show the specific screen instead of redirecting back to SSO login
      // and creating an infinite loop.
      const denial = parseAccessDenial(error?.response?.data);
      await tokenManager.clearTokens();
      setUser(null);
      setToken(null);
      if (denial) {
        setAccessDenial(denial);
        return;
      }
      redirectToSSOLogin();
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    isLoggedIn.current = false;
    try {
      await authService.logout();
    } catch {
      // Continue with local cleanup even if SSO logout fails
    }
    setUser(null);
    setToken(null);
    setAccessDenial(null);
    await tokenManager.clearTokens();
    // Redirect to SSO login
    redirectToSSOLogin();
  };

  const refreshUser = async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUserIfChanged(userData);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user,
        accessDenial,
        clearAccessDenial,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
